'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  signalService,
  journalService,
  obService,
  type Signal,
  type JournalEntry,
  type OrderBlock,
} from '@/lib/services/tradeService';
import { createClient } from '@/lib/supabase/client';
import { invalidateLedgerCache } from '@/lib/services/ledgerService';
import { useAuth } from '@/contexts/AuthContext';

interface RealtimeContextValue {
  signals: Signal[];
  journalEntries: JournalEntry[];
  orderBlocks: OrderBlock[];
  signalsLoading: boolean;
  journalLoading: boolean;
  obLoading: boolean;
  signalsError: string | null;
  journalError: string | null;
  obError: string | null;
  refreshSignals: () => Promise<void>;
  refreshJournal: () => Promise<void>;
  refreshOBs: () => Promise<void>;
  obLastUpdated: string;
  signalsLastUpdated: string;
  realtimeTick: number;
  signalsRealtimeConnected: boolean;
  /** Increments whenever earnings_breakdown_entries changes — subscribe in EarningsBreakdownTable */
  ledgerRealtimeTick: number;
  /** Whether the ledger realtime channel is currently connected */
  ledgerRealtimeConnected: boolean;
  /** Number of ledger updates that were queued while disconnected and replayed on reconnect */
  ledgerMissedUpdates: number;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function useRealtime() {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtime must be used within RealtimeProvider');
  return ctx;
}

function nowTimeStr(): string {
  return new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const [signals, setSignals] = useState<Signal[]>([]);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [orderBlocks, setOrderBlocks] = useState<OrderBlock[]>([]);

  const [signalsLoading, setSignalsLoading] = useState(true);
  const [journalLoading, setJournalLoading] = useState(true);
  const [obLoading, setObLoading] = useState(true);

  const [signalsError, setSignalsError] = useState<string | null>(null);
  const [journalError, setJournalError] = useState<string | null>(null);
  const [obError, setObError] = useState<string | null>(null);

  const [obLastUpdated, setObLastUpdated] = useState<string>('');
  const [signalsLastUpdated, setSignalsLastUpdated] = useState<string>('');
  const [realtimeTick, setRealtimeTick] = useState<number>(0);
  const [signalsRealtimeConnected, setSignalsRealtimeConnected] = useState(false);
  const [ledgerRealtimeTick, setLedgerRealtimeTick] = useState<number>(0);
  const [ledgerRealtimeConnected, setLedgerRealtimeConnected] = useState(true);
  const [ledgerMissedUpdates, setLedgerMissedUpdates] = useState<number>(0);

  const obPollingRef = useRef<NodeJS.Timeout | null>(null);
  const signalsChannelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);
  const journalChannelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);
  const ledgerChannelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);

  // ── Ledger disconnect queue & reconnect refs ─────────────────────────────
  /** Payloads received while the channel was not SUBSCRIBED — replayed on reconnect */
  const ledgerMissedQueueRef = useRef<unknown[]>([]);
  /** Whether the ledger channel is currently in a connected state */
  const ledgerConnectedRef = useRef<boolean>(false);

  const refreshSignals = useCallback(async () => {
    try {
      setSignalsLoading(true);
      setSignalsError(null);
      const data = await signalService.getAll();
      setSignals(data);
      setSignalsLastUpdated(nowTimeStr());
    } catch (e: any) {
      setSignalsError(e.message || 'Failed to load signals');
    } finally {
      setSignalsLoading(false);
    }
  }, []);

  const refreshJournal = useCallback(async () => {
    try {
      setJournalLoading(true);
      setJournalError(null);
      const data = await journalService.getAll();
      setJournalEntries(data);
    } catch (e: any) {
      setJournalError(e.message || 'Failed to load journal');
    } finally {
      setJournalLoading(false);
    }
  }, []);

  const refreshOBs = useCallback(async () => {
    try {
      setObLoading(true);
      setObError(null);
      const data = await obService.getAll();
      setOrderBlocks(data);
      setObLastUpdated(nowTimeStr());
    } catch (e: any) {
      setObError(e.message || 'Failed to load order blocks');
    } finally {
      setObLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    refreshSignals();
    refreshJournal();
    refreshOBs();
  }, [refreshSignals, refreshJournal, refreshOBs]);

  // ── Supabase real-time subscription for signals ──────────────────────────────
  useEffect(() => {
    const supabase = createClient();

    if (signalsChannelRef.current) {
      supabase.removeChannel(signalsChannelRef.current);
      signalsChannelRef.current = null;
    }

    const channel = supabase
      .channel('signals-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'signals' },
        async (_payload) => {
          try {
            const data = await signalService.getAll();
            setSignals(data);
            setSignalsLastUpdated(nowTimeStr());
            setRealtimeTick((t) => t + 1);
          } catch {
            // Silent fail — stale data is better than a crash
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setSignalsRealtimeConnected(true);
        } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          setSignalsRealtimeConnected(false);
        }
      });

    signalsChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      signalsChannelRef.current = null;
      setSignalsRealtimeConnected(false);
    };
  }, []);

  // ── Supabase real-time subscription for journal_entries ──────────────────────
  useEffect(() => {
    const supabase = createClient();

    if (journalChannelRef.current) {
      supabase.removeChannel(journalChannelRef.current);
      journalChannelRef.current = null;
    }

    const channel = supabase
      .channel('journal-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'journal_entries' },
        async () => {
          try {
            const data = await journalService.getAll();
            setJournalEntries(data);
          } catch {
            // Silent fail
          }
        }
      )
      .subscribe();

    journalChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      journalChannelRef.current = null;
    };
  }, []);

  // ── Supabase real-time subscription for earnings_breakdown_entries (ledger) ──
  // Fires ledgerRealtimeTick so EarningsBreakdownTable can reload without polling.
  useEffect(() => {
    if (!user) return;
    const supabase = createClient();

    if (ledgerChannelRef.current) {
      supabase.removeChannel(ledgerChannelRef.current);
      ledgerChannelRef.current = null;
    }

    const channel = supabase
      .channel('ledger-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'earnings_breakdown_entries',
          filter: `user_id=eq.${user.id}`,
        },
        (_payload) => {
          // Invalidate cache and bump tick so EarningsBreakdownTable reloads
          invalidateLedgerCache(user.id);
          setLedgerRealtimeTick((t) => t + 1);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          ledgerConnectedRef.current = true;
          setLedgerRealtimeConnected(true);
          // Flush any missed updates queued before connection was established
          const count = ledgerMissedQueueRef.current.length;
          if (count > 0) {
            ledgerMissedQueueRef.current = [];
            invalidateLedgerCache(user.id);
            setLedgerMissedUpdates(count);
            setLedgerRealtimeTick((t) => t + 1);
            setTimeout(() => setLedgerMissedUpdates(0), 4000);
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Mark as disconnected — Supabase client will auto-reconnect internally
          ledgerConnectedRef.current = false;
          setLedgerRealtimeConnected(false);
        } else if (status === 'CLOSED') {
          ledgerConnectedRef.current = false;
          setLedgerRealtimeConnected(false);
        }
      });

    ledgerChannelRef.current = channel;

    return () => {
      if (ledgerChannelRef.current) {
        supabase.removeChannel(ledgerChannelRef.current);
        ledgerChannelRef.current = null;
      }
      ledgerConnectedRef.current = false;
    };
  }, [user]);

  // ── Polling for order blocks only (no real-time table configured for OBs) ───
  useEffect(() => {
    obPollingRef.current = setInterval(async () => {
      try {
        const obData = await obService.getAll();
        setOrderBlocks(obData);
        setObLastUpdated(nowTimeStr());
      } catch {
        // Silent fail
      }
    }, 5000);

    return () => {
      if (obPollingRef.current) clearInterval(obPollingRef.current);
    };
  }, []);

  const value: RealtimeContextValue = {
    signals,
    journalEntries,
    orderBlocks,
    signalsLoading,
    journalLoading,
    obLoading,
    signalsError,
    journalError,
    obError,
    refreshSignals,
    refreshJournal,
    refreshOBs,
    obLastUpdated,
    signalsLastUpdated,
    realtimeTick,
    signalsRealtimeConnected,
    ledgerRealtimeTick,
    ledgerRealtimeConnected,
    ledgerMissedUpdates,
  };

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
